// How big an exported playable is going to be.
//
// There are two answers, and they are not the same kind of answer.
//
// MEASURED — the size of the html a build actually produced, recorded by the
// server (see controller/snapshotEditor/buildSizes.js) and offered back per
// snapshot as `builds`. A measurement counts only while the snapshot file has
// not changed since: `fresh` says so.
//
// ESTIMATED — for a snapshot nobody has built yet. The server works this out
// for the snapshot being OPENED, by running the engine's own asset rules over
// it (controller/snapshotEditor/exportSizeEstimate.js), and hands it back with
// the snapshot; that number is the one to prefer. The rule of thumb below is
// the fallback for every other snapshot — the ones in a listing, which are not
// parsed — and it only knows how big the file is, so it reads high: an export
// drops the assets nothing uses, ships one language and subsets the fonts.
export const PLAY_ENGINE_RUNTIME_BYTES = 971 * 1024;

export function estimatePlayableBytes(snapshotBytes) {
  if (!snapshotBytes) return null;
  return snapshotBytes + PLAY_ENGINE_RUNTIME_BYTES;
}

/**
 * The measured size of one build of a snapshot, or null when that pair has not
 * been built from the bytes that are on disk now.
 *
 * @param {object} summary a listSnapshots entry
 * @param {string} network build name, as the export sends it
 * @param {string} language language code, or "all"
 */
export function measuredPlayableBytes(summary, network, language) {
  const builds = (summary && summary.builds) || [];
  const match = builds.find(
    (build) => build.fresh && build.network === network && build.language === language
  );
  return match ? match.bytes : null;
}

/**
 * The best answer available for one snapshot in one build, in the order the
 * answers deserve: what a build of it actually measured, then the server's
 * estimate — which parsed the snapshot and ran the engine's own asset rules
 * over it — then the rule of thumb, which only knows the file's size.
 *
 * ONE RULE, BECAUSE THERE ARE TWO SCREENS. The preview toolbar and the export
 * dialog answer the same question about the same snapshot, and they used to
 * answer it differently: the toolbar followed this order while the dialog
 * reached straight for the rule of thumb, so an open snapshot read ~2.4 MB
 * above the preview and ~3.0 MB in the export dialog. Whichever number is
 * right, two of them on one screen is wrong.
 *
 * @param {object}  options.summary        the snapshot's listing entry, for measurements
 * @param {string}  options.network        build name, as the export sends it
 * @param {string}  options.language       language code, or "all"
 * @param {number?} options.serverEstimate the open snapshot's `estimatedBytes`, when this is it
 * @param {number?} options.snapshotBytes  the snapshot file's size
 */
export function bestPlayableBytes({ summary, network, language, serverEstimate, snapshotBytes }) {
  return (
    measuredPlayableBytes(summary, network, language) ||
    serverEstimate ||
    estimatePlayableBytes(snapshotBytes) ||
    null
  );
}

/**
 * What the export dialog shows: the sum over every cell of the matrix, built
 * from measurements where there are any and estimates where there are not.
 *
 * `measured` and `estimated` count cells, so the dialog can say which kind of
 * number it is showing. `unknown` counts the cells it could say nothing about
 * — a snapshot whose size the listing does not carry — so a total missing a
 * part does not read as a complete one.
 *
 * @param {object[]} summaries the selected snapshots' listing entries
 * @param {string[]} networks
 * @param {string[]|Function} languages one entry, "all", in device-language
 *   mode; a function when each snapshot is built in its own languages
 */
export function playableSizeTotal(summaries, networks, languages) {
  const languagesOf = typeof languages === "function" ? languages : () => languages;
  let bytes = 0;
  let measured = 0;
  let estimated = 0;
  let unknown = 0;

  for (const summary of summaries) {
    for (const network of networks) {
      for (const language of languagesOf(summary)) {
        const real = measuredPlayableBytes(summary, network, language);
        if (real) {
          bytes += real;
          measured += 1;
          continue;
        }
        const guess = estimatePlayableBytes(summary && summary.sizeBytes);
        if (guess) {
          bytes += guess;
          estimated += 1;
        } else {
          unknown += 1;
        }
      }
    }
  }

  return { bytes, measured, estimated, unknown };
}

// Decimal units, the way Finder and the ad networks' size limits count them.
export function formatBytes(bytes) {
  if (!isFinite(bytes) || bytes < 0) return "";
  const kb = bytes / 1000;
  return kb >= 1000 ? `${(kb / 1000).toFixed(1)} MB` : `${Math.round(kb)} KB`;
}
